<template>
  <div>
    <div class="tw-flex tw-justify-between tw-items-center">
      <base-text-field v-model="query" ref="searchInput" class="tw-flex-grow" placeholder="Search for a program" size="sm" tabindex="1">
        <template #prefix>
          <search-icon class="tw-h-5 tw-w-5" />
        </template>
      </base-text-field>
      <base-button class="tw-ml-5 tw-hidden sm:tw-flex" type="primary" @click="add">
        <template v-slot:icon>
          <plus-icon />
        </template>
        Add Program
      </base-button>
      <base-icon @click="add" class="tw-ml-3 tw-inline sm:tw-hidden">
        <plus-icon color="green" class="tw-h-5 tw-w-5" />
      </base-icon>
    </div>
    <base-table :loading="isLoading" :fields="fields" :items="items" class="tw-mt-5">
      <template #program="{item: program}">
        <div class="tw-whitespace-nowrap">{{ program.name }}</div>
        <div class="tw-text-gray-500 tw-text-xs">{{ program.programGroup }}</div>
      </template>
      <template #maxMedications="{item: program}">
        <div>{{ program.maxMedications }}</div>
      </template>
      <template #allowedMedications="{item: program}">
        <div>{{ allowedMedicationsCount(program.allowedMedications) }}</div>
      </template>
      <template #actions="{item: program}">
        <div class="tw-flex tw-items-center tw-space-x-2">
          <base-button type="flat" @click="edit(program)">
            <template v-slot:icon>
              <edit-icon /> </template
            >Edit
          </base-button>
          <base-button type="flat" @click="editMappings(program)">
            <template v-slot:icon>
              <mapping-icon /> </template
            >Mappings
          </base-button>
          <base-button type="flat" @click="openOnboarding(program)">
            <template v-slot:icon>
              <video-icon /> </template
            >Onboarding
          </base-button>
          <base-button type="flat" @click="del(program)">
            <template v-slot:icon>
              <trash-icon /> </template
            >Delete
          </base-button>
        </div>
      </template>
      <template #empty-state>
        <div class="tw-flex tw-justify-center tw-py-5">
          No Results
        </div>
      </template>
      <template #loading-row>
        <td class="tw-py-3 tw-px-4">
          <div class="tw-flex tw-flex-col tw-w-full">
            <base-skeleton-loader class="tw-h-4 tw-w-full tw-mb-2" />
            <base-skeleton-loader class="tw-h-3 tw-w-16" />
          </div>
        </td>
        <td class="tw-py-3 tw-px-4" align="right">
          <base-skeleton-loader class="tw-h-4 tw-w-4 tw-mb-2" />
        </td>
        <td class="tw-py-3 tw-px-4" align="right">
          <base-skeleton-loader class="tw-h-4 tw-w-4 tw-mb-2" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <div class="tw-flex tw-space-x-6 tw-justify-center">
            <base-skeleton-loader class="tw-h-6 tw-w-12" />
            <base-skeleton-loader class="tw-h-6 tw-w-16" />
            <base-skeleton-loader class="tw-h-6 tw-w-16" />
            <base-skeleton-loader class="tw-h-6 tw-w-12" />
          </div>
        </td>
      </template>
    </base-table>
    <base-pagination v-model="page" class="tw-mt-2" />
    <business-program-delete-confirmation-modal ref="deleteConfirmationModal" @delete="loadPage" />
    <business-program-slideover ref="slideover" :businessId="businessId" @save="loadPage" />
    <business-program-mappings-slideover ref="mappingsSlideover" :business-id="businessId" />
    <upload-onboarding-documents ref="onboardingSlideover" :business-id="businessId" />
  </div>
</template>

<script>
import BusinessProgramDeleteConfirmationModal from "@/views/business/business-programs/components/BusinessProgramDeleteConfirmationModal";
import BusinessProgramSlideover from "@/views/business/business-programs/components/BusinessProgramSlideover";
import BusinessProgramMappingsSlideover from "@/views/business/business-programs/components/BusinessProgramMappingsSlideover";
import UploadOnboardingDocuments from "@/views/business/business-programs/components/UploadOnboardingDocuments";
import { PlusIcon, EditIcon, TrashIcon, SearchIcon, MappingIcon, VideoIcon } from "@/assets/svgs";
import { debounce } from "debounce";
import { orderBy } from "lodash";

const TAKE_KEY = "predictile_business_programs_take";

export default {
  components: {
    BusinessProgramDeleteConfirmationModal,
    BusinessProgramSlideover,
    BusinessProgramMappingsSlideover,
    UploadOnboardingDocuments,
    PlusIcon,
    EditIcon,
    TrashIcon,
    SearchIcon,
    MappingIcon,
    VideoIcon,
  },
  props: {
    businessId: String,
  },
  data() {
    return {
      items: [],
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
      fields: [
        { title: "Program", key: "program", thClass: "" },
        { title: "Max Medications", key: "maxMedications", thClass: "tw-w-0 xl:tw-whitespace-nowrap", align: "right" },
        { title: "Allowed Medications", key: "allowedMedications", thClass: "tw-w-0 xl:tw-whitespace-nowrap", align: "right" },
        { title: "Actions", key: "actions", thClass: "tw-w-0 tw-text-center" },
      ],
      query: null,
      isLoading: false,
      programGroups: [],
      programCategories: [],
    };
  },
  computed: {},
  created() {
    this.loadPage();
    this.debouncedLoadPage = debounce(this.loadPage, 500);
  },
  mounted() {
    const searchInputRef = this.$refs.searchInput;
    searchInputRef.focus();
  },
  watch: {
    page(newValue) {
      localStorage.setItem(TAKE_KEY, newValue.take);
      this.loadPage();
    },
    query() {
      this.page.skip = 0;
      this.debouncedLoadPage();
    },
  },
  methods: {
    allowedMedicationsCount(allowedMedications) {
      return allowedMedications ? allowedMedications.length : 0;
    },
    add() {
      this.$refs.slideover.show(this.businessId, null, this.programGroups, this.programCategories);
    },
    edit(item) {
      this.$refs.slideover.show(this.businessId, item, this.programGroups, this.programCategories);
    },
    editMappings(item) {
      this.$refs.mappingsSlideover.show(item);
    },
    openOnboarding(item) {
      this.$refs.onboardingSlideover.show(item);
    },
    del(item) {
      this.$refs.deleteConfirmationModal.show(this.businessId, item.businessProgramId, item.name);
    },
    next() {
      this.page.skip += this.page.take;
    },
    previous() {
      this.page.skip -= this.page.take;
    },
    loadPage() {
      this.isLoading = true;

      let url = `/businesses/${this.businessId}/programs/search`;
      url += `?skip=${this.page.skip}&take=${this.page.take}`;
      if (this.query) url += `&query=${encodeURIComponent(this.query)}`;

      this.$axios
        .get(url)
        .then((res) => {
          this.page.total = res.data.total;
          this.items = res.data.items;
          if (this.items.length !== 0) {
            this.items = orderBy(this.items, ["name", "programGroup"], ["asc", "asc"]);
          }
          this.programGroups = this.getGroups(res.data.items);
          this.programCategories = this.getCategories(res.data.items);
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: `Failed to load products`,
            message: `We had a problem retrieving the list of products. Please reload the page to try again.`,
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getGroups(items) {
      let groups = [...new Map(items.map((item) => [item["programGroup"], { value: item["programGroup"], label: item["programGroup"] }])).values()].filter((x) => x.value !== null);
      groups.push({ value: "new group", label: "+ Add New Group" });
      return groups;
    },
    getCategories(items) {
      let categories = [...new Map(items.map((item) => [item["programCategory"], { value: item["programCategory"], label: item["programCategory"] }])).values()].filter(
        (x) => x.value !== null,
      );
      categories.push({ value: "new category", label: "+ Add New Category" });
      return categories;
    },
  },
};
</script>
